import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
  useLayoutEffect,
} from 'react'
import { SvgAccordionArrow } from '../../assets/images'
import { noop } from 'lodash'
import useEffectAfterFirstRender from '../hooks/useEffectAfterFirstRender'
import { ShortcutKeys } from '../../utils/tab-indexing-contants'

export interface AccordionPropType {
  heading: ReactNode
  isDefaultActive?: boolean
  children: React.ReactNode
  className?: string
  isDisabled?: boolean
  triggerOpen?: boolean
  trigger?: { isOpen: boolean }
  borderColor?: string
  alwaysOpen?: boolean
  accordianHeightPadding?: string
  handleAccordionClick?: (accordionExpanded: boolean) => void
  isConditional?: boolean
  id?: string
  tabIndex?: number
  closedAccordionDisplay?: string
  customStyles?: object
}

const Accordion: React.FC<AccordionPropType> = ({
  heading,
  isDefaultActive = false,
  children,
  className = '',
  isDisabled = false,
  triggerOpen = false,
  trigger,
  borderColor = 'secondary-450',
  alwaysOpen = false,
  accordianHeightPadding = 'px-5 h-10',
  handleAccordionClick,
  isConditional = false,
  id,
  tabIndex,
  closedAccordionDisplay = 'none',
  customStyles = {},
}) => {
  const [accordionExpanded, setAccordionExpanded] = useState<boolean>(
    isDisabled ? false : isDefaultActive || trigger?.isOpen === true
  )
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (contentRef?.current) {
      const isOpen = !isDisabled && (accordionExpanded || alwaysOpen)
      contentRef.current.style.maxHeight = isOpen
        ? `${contentRef.current.scrollHeight}px`
        : '0px'
      setTimeout(() => {
        if (contentRef?.current) {
          contentRef.current.style.maxHeight = isOpen ? `100%` : '0px'
          contentRef.current.style.height = isOpen ? `100%` : '0px'
        }
      }, 250)
      contentRef.current.style.display = isOpen
        ? 'block'
        : closedAccordionDisplay ?? 'none'
    }
  }, [accordionExpanded])

  useEffect(() => {
    if (triggerOpen) {
      setAccordionExpanded(true)
    }
  }, [triggerOpen])

  useEffect(() => {
    if (trigger) {
      if (trigger.isOpen) setAccordionExpanded(true)
      else setAccordionExpanded(false)
    }
  }, [trigger])

  const handleToggle = () => {
    setAccordionExpanded((prev) => !prev)
  }

  useEffectAfterFirstRender(() => {
    handleAccordionClick && handleAccordionClick(accordionExpanded)
  }, [accordionExpanded])

  return (
    <div
      className="bg-white font-primary overflow-hidden border border-accent-200 text-black rounded h-fit max-h-fit flex-col my-1"
      id={id}
      style={{ ...customStyles }}
    >
      <div
        className={`header cursor-pointer items-center rounded  border border-y-accent-300 border-r-accent-300  flex border-l-4 border-${borderColor} ${className} ${accordianHeightPadding}`}
        onClick={isDisabled || alwaysOpen ? noop : handleToggle}
        style={{
          borderLeftColor: borderColor.startsWith('#') ? borderColor : ``,
        }}
        tabIndex={tabIndex ?? 0}
        onKeyDown={(e) => {
          if (
            (e?.key === ShortcutKeys.Space || e?.key === ShortcutKeys.Enter) &&
            !(isDisabled || alwaysOpen)
          ) {
            e?.preventDefault()
            handleToggle()
          }
        }}
        role="button"
        aria-expanded={accordionExpanded}
        aria-disabled={isDisabled}
      >
        <div className="flex-1 text-sm font-normal mr-3">{heading}</div>
        {!alwaysOpen ? (
          <div>
            <SvgAccordionArrow
              className={`transform transition-transform duration-200  ${
                accordionExpanded ? 'rotate-180' : ''
              } ${isDisabled || alwaysOpen ? 'invisible' : ''}`}
              id={id}
            />
          </div>
        ) : null}
      </div>
      <div
        className={`transition-all hide-scrollbar duration-350 ease-in-out`}
        // style={{ maxHeight }}
        ref={contentRef}
      >
        {isConditional ? (
          <div className="h-[90%] hide-scrollbar overflow-auto">
            {accordionExpanded ? children : null}
          </div>
        ) : (
          <div className="h-[90%] hide-scrollbar overflow-auto">{children}</div>
        )}
      </div>
    </div>
  )
}

export default Accordion
